import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

/**
 * Asistente de puesta en marcha (19-sep-2026, petición de Luis): recuerda -sin
 * regañar- a los negocios que se registraron y dejaron su perfil a medias
 * (sin dirección, sin foto, sin página web...). Contrato construido por el
 * agente backend en paralelo:
 *   GET /setup-tasks               → { hechas, total, tareas[], siguiente }
 *   POST /setup-tasks/:id/mas-tarde → aplaza 3 días y suma uno; al tercero
 *                                      queda descartada para siempre (deja de
 *                                      salir flotando, sigue en el Panel).
 *
 * `SetupAssistantPopup` (flotante) y `SetupTasksCard` (tarjeta del Panel)
 * comparten esta misma queryKey para no duplicar la petición — mismo
 * criterio que `SUBSCRIPTION_QUERY_KEY` en `useSubscription.ts`.
 */

export interface SetupTask {
  id: string;
  titulo: string;
  mensaje: string;
  ctaTexto: string;
  ctaRuta: string;
  hecha: boolean;
  aplazadaHasta: string | null;
  vecesAplazada: number;
  descartada: boolean;
}

export interface SetupTasksData {
  hechas: number;
  total: number;
  tareas: SetupTask[];
  siguiente: SetupTask | null;
}

export const SETUP_TASKS_QUERY_KEY = ["organization", "setup-tasks"] as const;

/**
 * El backend puede envolver la respuesta en `{success, data}` (patrón
 * habitual del resto de la API) o devolver el objeto suelto tal cual lo
 * describe el contrato — no se asume ninguna de las dos formas mientras el
 * endpoint lo construye otro agente en paralelo.
 */
function unwrapSetupTasks(raw: unknown): SetupTasksData {
  if (raw && typeof raw === "object" && "data" in raw) {
    const wrapped = (raw as { data?: unknown }).data;
    if (wrapped && typeof wrapped === "object" && "tareas" in wrapped) {
      return wrapped as SetupTasksData;
    }
  }
  return raw as SetupTasksData;
}

function unwrapSetupTask(raw: unknown): SetupTask {
  if (raw && typeof raw === "object" && "data" in raw) {
    const wrapped = (raw as { data?: unknown }).data;
    if (wrapped && typeof wrapped === "object" && "id" in wrapped) {
      return wrapped as SetupTask;
    }
  }
  return raw as SetupTask;
}

async function fetchSetupTasks(): Promise<SetupTasksData> {
  const response = await apiClient.get("/setup-tasks");
  return unwrapSetupTasks(response.data);
}

/**
 * `enabled`: el asistente es cosa de quien puede editar el perfil del
 * negocio (admin/owner) — mismo criterio de rol que `TrialBanner`/
 * `AppDiscoveryBanner`. Con `enabled=false` no se lanza la petición (staff).
 *
 * `retry: false` a propósito: si el endpoint aún no existe (lo construye
 * otro agente en paralelo) o falla, debe fallar rápido y en silencio — ni el
 * flotante ni la tarjeta del panel pueden enseñar nunca un error, solo no
 * pintarse (ambos consumidores comprueban `data` antes de renderizar).
 */
export function useSetupTasks(enabled: boolean) {
  return useQuery({
    queryKey: SETUP_TASKS_QUERY_KEY,
    queryFn: fetchSetupTasks,
    enabled,
    retry: false,
  });
}

/**
 * POST /setup-tasks/:id/mas-tarde. Tras aplazar se invalida la query entera
 * en vez de recalcular `siguiente` a mano en el cliente: solo el backend
 * conoce el criterio real de selección (aplazadaHasta, vecesAplazada...) y
 * replicarlo aquí divergiría tarde o temprano.
 */
export function useSnoozeSetupTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post(`/setup-tasks/${id}/mas-tarde`);
      return unwrapSetupTask(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETUP_TASKS_QUERY_KEY });
    },
    // Silencioso a propósito: aplazar es una acción de bajo riesgo, si falla
    // la tarea simplemente se lo pedirá de nuevo en otra sesión.
    onError: () => {},
  });
}
